import { useEffect, useState } from "react";
import { useParams, useSearchParams, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Calendar, MapPin, Clock, Heart, Sparkles, Gift, Wine, 
  Check, ChevronDown, ChevronUp, X, ArrowRight, Car, 
  Shirt, Timer, Star, Users, Ticket, Shield, BadgeCheck, 
  Lock, CreditCard
} from "lucide-react";
import axios from "axios";
import { trackViewContent, trackInitiateCheckout, trackPurchase } from "../utils/metaTracking";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Generate or retrieve session ID for analytics
const getSessionId = () => {
  let sessionId = sessionStorage.getItem("analytics_session_id");
  if (!sessionId) {
    sessionId = `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem("analytics_session_id", sessionId);
  }
  return sessionId;
};

// Analytics tracking helper
const trackEvent = async (eventType, data = {}) => {
  try {
    const sessionId = getSessionId();
    await axios.post(`${API}/public/track-${eventType}`, {
      ...data,
      session_id: sessionId,
      timestamp: new Date().toISOString()
    });
  } catch (err) {
    // Silent fail for tracking - don't disrupt user experience
    console.debug("Analytics tracking:", eventType, err.message);
  }
};

const DynamicEventLandingPage = () => {
  const { slug: paramSlug, landingSlug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const rawSlug = paramSlug || landingSlug || location.pathname.slice(1);

  // Detect clean success URL: /suds-1-success → slug="suds-1", isSuccessPage=true
  const isSuccessPage = rawSlug.endsWith('-success');
  const slug = isSuccessPage ? rawSlug.replace(/-success$/, '') : rawSlug;

  const [searchParams] = useSearchParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [checkoutForm, setCheckoutForm] = useState({
    name: "",
    email: "",
    phone: "",
    quantity: 1,
    couponCode: ""
  });
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [couponApplied, setCouponApplied] = useState(null);
  const [couponError, setCouponError] = useState(null);
  const [paymentResult, setPaymentResult] = useState(null); // { status: 'success'|'failed'|'processing', booking: {...} }

  // Track page view on mount
  useEffect(() => {
    trackEvent("pageview", { page_slug: slug });
  }, [slug]);

  // Track clicks for heatmap - attach global click listener
  useEffect(() => {
    const handleGlobalClick = (e) => {
      const target = e.target.closest('button, a, [data-track]');
      if (!target) return;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
      const x_percent = ((e.clientX + scrollLeft) / document.documentElement.scrollWidth) * 100;
      const y_percent = ((e.clientY + scrollTop) / document.documentElement.scrollHeight) * 100;
      let element = target.dataset?.track || target.dataset?.testid || 'unknown';
      if (target.tagName === 'BUTTON') {
        const text = target.textContent?.trim().slice(0, 30) || 'button';
        element = `button_${text.toLowerCase().replace(/\s+/g, '_')}`;
      } else if (target.tagName === 'A') {
        element = `link_${target.href?.split('/').pop() || 'link'}`;
      }
      trackEvent("click", { page_slug: slug, element, x_percent: Math.round(x_percent * 100) / 100, y_percent: Math.round(y_percent * 100) / 100 });
    };
    document.addEventListener('click', handleGlobalClick);
    return () => document.removeEventListener('click', handleGlobalClick);
  }, [slug]);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        const res = await axios.get(`${API}/public/event-page/${slug}`);
        setPage(res.data);
        document.title = `${res.data.title} | Mix & Mingle`;
        // Meta: ViewContent when CMS event landing page loads
        // Reads title and slug from CMS data — reusable for any /:slug page
        const lowestPrice = res.data.ticket_types?.reduce((min, t) => Math.min(min, t.price || 0), Infinity) || 0;
        trackViewContent({
          slug,
          title: res.data.title || slug,
          businessLine: 'Event',
          value: lowestPrice,
          currency: 'CAD',
        });
      } catch (err) {
        console.error("Error fetching page:", err);
        setError("Event page not found");
      } finally {
        setLoading(false);
      }
    };
    fetchPage();
  }, [slug]);

  // ─── Payment confirmation: handles BOTH clean URL and old fallback ───
  useEffect(() => {
    const ref = searchParams.get("ref");

    // NEW clean URL: /{slug}-success?ref={booking_code}
    if (isSuccessPage && ref) {
      const confirmByRef = async () => {
        try {
          const res = await axios.get(`${API}/event-checkout/confirm/${ref}`);
          const data = res.data;
          if (data.status === "paid" || data.status === "complete") {
            setPaymentResult({ status: "success", booking: data.booking, email_sent: data.email_sent, admin_notified: data.admin_notified });
            trackEvent("conversion", { page_slug: slug, event_type: "checkout_complete", value: data.booking?.total_amount || 0 });
            const b = data.booking;
            if (b) {
              trackPurchase({
                orderId: b.id || ref,
                bookingCode: b.booking_code || '',
                name: b.name || '',
                email: b.email || '',
                phone: b.phone || '',
                title: b.page_title || page?.title || slug,
                slug,
                value: b.total_amount || 0,
                currency: 'CAD',
                quantity: b.quantity || 1,
                businessLine: 'events',
                ticketType: b.ticket_type || '',
              });
            }
          } else if (data.status === "pending") {
            setPaymentResult({ status: "processing", booking: data.booking, message: data.message });
          } else {
            setPaymentResult({ status: "success", booking: data.booking, email_sent: data.email_sent });
          }
        } catch {
          setPaymentResult({ status: "processing", booking: null, message: "Your payment is being confirmed. You will receive a confirmation email shortly." });
        }
      };
      confirmByRef();
      return;
    }

    // OLD fallback: /{slug}?success=true&session_id=... → validate then redirect to clean URL
    const sessionId = searchParams.get("session_id");
    if (searchParams.get("success") === "true" && sessionId) {
      const confirmAndRedirect = async () => {
        try {
          const res = await axios.get(`${API}/event-checkout/status/${sessionId}`);
          const data = res.data;
          const bookingCode = data.booking?.booking_code;
          if (bookingCode) {
            // Redirect to the clean URL (browser replaces history entry)
            navigate(`/${slug}-success?ref=${bookingCode}`, { replace: true });
          } else {
            // No booking code available, show inline
            setPaymentResult({ status: data.status === "paid" ? "success" : "processing", booking: data.booking });
          }
        } catch {
          setPaymentResult({ status: "processing", booking: null, message: "Your payment is being confirmed. You will receive a confirmation email shortly." });
        }
      };
      confirmAndRedirect();
      return;
    }

    if (searchParams.get("success") === "true") {
      setPaymentResult({ status: "success", booking: null });
    }

    if (searchParams.get("canceled") === "true") {
      setPaymentResult({ status: "failed" });
      trackEvent("conversion", { page_slug: slug, event_type: "checkout_canceled", value: 0 });
    }
  }, [searchParams, slug, isSuccessPage, navigate]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const [year, month, day] = dateStr.split("-").map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric"
    });
  };

  const getIcon = (iconName) => {
    const icons = {
      soap: <Sparkles className="w-6 h-6" />,
      wine: <Wine className="w-6 h-6" />,
      sparkle: <Sparkles className="w-6 h-6" />,
      gift: <Gift className="w-6 h-6" />
    };
    return icons[iconName] || <Sparkles className="w-6 h-6" />;
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const openCheckout = (ticket) => {
    setSelectedTicket(ticket);
    setCheckoutForm(prev => ({ ...prev, quantity: ticket.quantity || 1 }));
    setShowCheckoutModal(true);
    setCouponApplied(null);
    setCouponError(null);
    // Track ticket selection as a conversion step
    trackEvent("conversion", { 
      page_slug: slug, 
      event_type: "ticket_selected",
      ticket_type: ticket.name,
      value: ticket.price
    });
  };

  const validateCoupon = async () => {
    if (!checkoutForm.couponCode) return;
    
    try {
      const res = await axios.post(`${API}/coupons/validate`, {
        code: checkoutForm.couponCode,
        type: "landing_pages",
        landing_page_slug: slug
      });
      if (res.data.valid) {
        setCouponApplied(res.data);
        setCouponError(null);
        toast.success("Coupon applied!");
      } else {
        setCouponError(res.data.message || "Invalid coupon");
        setCouponApplied(null);
      }
    } catch (err) {
      setCouponError(err.response?.data?.detail || "Failed to validate coupon");
      setCouponApplied(null);
    }
  };

  const calculateTotal = () => {
    if (!selectedTicket) return 0;
    // Group tickets (with seats) have a fixed total price
    const subtotal = selectedTicket.seats 
      ? selectedTicket.price 
      : selectedTicket.price * checkoutForm.quantity;
    
    if (couponApplied) {
      if (couponApplied.discount_type === "percentage") {
        return subtotal - (subtotal * couponApplied.discount_value / 100);
      }
      return Math.max(0, subtotal - couponApplied.discount_value);
    }
    return subtotal;
  };

  const handleCheckout = async () => {
    if (!checkoutForm.name || !checkoutForm.email || !checkoutForm.phone) {
      toast.error("Please fill in all required fields");
      return;
    }

    setCheckoutLoading(true);
    try {
      // Track checkout initiation
      trackEvent("conversion", { 
        page_slug: slug, 
        event_type: "checkout_initiated",
        ticket_type: selectedTicket.name,
        value: calculateTotal()
      });

      // Meta: InitiateCheckout
      trackInitiateCheckout({
        slug,
        title: page?.title || slug,
        ticketType: selectedTicket.name,
        quantity: selectedTicket.seats ? 1 : checkoutForm.quantity,
        value: calculateTotal(),
        currency: 'CAD',
        businessLine: 'events',
      });

      const res = await axios.post(`${API}/event-checkout/create-session`, {
        page_slug: slug,
        ticket_type: selectedTicket.name,
        ticket_id: selectedTicket.id,
        // For group tickets, quantity is 1 (the price is already for the group)
        quantity: selectedTicket.seats ? 1 : checkoutForm.quantity,
        name: checkoutForm.name,
        email: checkoutForm.email,
        phone: checkoutForm.phone,
        origin_url: window.location.origin,
        coupon_code: couponApplied ? checkoutForm.couponCode : null
      });

      if (res.data.free_ticket) {
        // Free ticket - coupon covered the full amount
        trackEvent("conversion", { 
          page_slug: slug, 
          event_type: "checkout_complete",
          ticket_type: selectedTicket.name,
          value: 0
        });
        toast.success(res.data.message || "Booking confirmed! Your ticket is free.");
        // Redirect to clean success page
        const bookingCode = res.data.booking_code || res.data.booking_id;
        navigate(`/${slug}-success?ref=${bookingCode}`, { replace: true });
      } else if (res.data.checkout_url) {
        // Track successful redirect to Stripe
        trackEvent("conversion", { 
          page_slug: slug, 
          event_type: "stripe_redirect",
          value: calculateTotal()
        });
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      console.error("Checkout error:", err);
      toast.error(err.response?.data?.detail || "Failed to create checkout");
    } finally {
      setCheckoutLoading(false);
    }
  };

  // On success pages, show loading until payment result is ready
  // (CMS page fetch may fail for success slugs — that's fine)
  if (loading && !isSuccessPage) {
    return (
      <main className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#D4AF37] border-t-transparent"></div>
      </main>
    );
  }

  if (isSuccessPage && !paymentResult) {
    return (
      <main className="min-h-screen bg-[#0F0F0F] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#D4AF37] border-t-transparent"></div>
      </main>
    );
  }

  // Payment Result Overlay - shows after Stripe redirect or clean success URL
  if (paymentResult) {
    const b = paymentResult.booking;
    const isSuccess = paymentResult.status === "success";
    const isProcessing = paymentResult.status === "processing";
    const isFailed = paymentResult.status === "failed";
    
    return (
      <main className="min-h-screen bg-[#0F0F0F] flex items-center justify-center p-4" data-testid="payment-result-page">
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }} 
          animate={{ opacity: 1, scale: 1 }} 
          className="max-w-lg w-full"
        >
          {/* Status Icon */}
          <div className="text-center mb-8">
            {isSuccess && (
              <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-10 h-10 text-green-400" />
              </div>
            )}
            {isProcessing && (
              <div className="w-20 h-20 bg-[#D4AF37]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#D4AF37] border-t-transparent" />
              </div>
            )}
            {isFailed && (
              <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <X className="w-10 h-10 text-red-400" />
              </div>
            )}
            
            <h1 className="text-3xl font-bold text-[#F5F5F0] mb-2" data-testid="payment-result-title">
              {isSuccess && "Thank You! Payment Received"}
              {isProcessing && "Payment Being Confirmed..."}
              {isFailed && "Payment Was Cancelled"}
            </h1>
            <p className="text-[#A1A1AA]">
              {isSuccess && "Your booking is confirmed. A confirmation email with your ticket has been sent."}
              {isProcessing && (paymentResult.message || "Your payment is being processed. You will receive a confirmation email shortly.")}
              {isFailed && "Your payment was not completed. No charges were made. You can try again anytime."}
            </p>
          </div>
          
          {/* Booking Details Card (for success/processing) */}
          {b && (isSuccess || isProcessing) && (
            <div className="bg-[#1A1A1A] border border-[#333] rounded-lg overflow-hidden mb-6">
              <div className="bg-gradient-to-r from-[#D4AF37] to-[#B8860B] px-6 py-4">
                <h2 className="text-[#050505] font-bold text-lg">{b.page_title || page?.title || "Event Ticket"}</h2>
                <p className="text-[#050505]/70 text-sm">Booking Reference: {(b.id || "").slice(0, 8).toUpperCase()}</p>
              </div>
              <div className="p-6 space-y-3">
                <div className="flex justify-between">
                  <span className="text-[#A1A1AA]">Guest</span>
                  <span className="text-[#F5F5F0] font-medium">{b.name}</span>
                </div>
                {b.email && (
                  <div className="flex justify-between">
                    <span className="text-[#A1A1AA]">Email</span>
                    <span className="text-[#F5F5F0]">{b.email}</span>
                  </div>
                )}
                {b.phone && (
                  <div className="flex justify-between">
                    <span className="text-[#A1A1AA]">Phone</span>
                    <span className="text-[#F5F5F0]">{b.phone}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-[#A1A1AA]">Ticket</span>
                  <span className="text-[#F5F5F0] font-medium">{b.ticket_type} x {b.quantity}</span>
                </div>
                {b.coupon_code && parseFloat(b.discount_amount) > 0 && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-[#A1A1AA]">Subtotal</span>
                      <span className="text-[#F5F5F0]">C${parseFloat(b.subtotal).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#A1A1AA]">Coupon ({b.coupon_code})</span>
                      <span className="text-green-400">-C${parseFloat(b.discount_amount).toFixed(2)}</span>
                    </div>
                  </>
                )}
                <div className="border-t border-[#333] pt-3 flex justify-between">
                  <span className="text-[#F5F5F0] font-bold">Total Paid</span>
                  <span className="text-[#D4AF37] font-bold text-xl">C${parseFloat(b.total_amount || 0).toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}
          
          {/* Check email notice */}
          {(isSuccess || isProcessing) && (
            <div className="bg-[#1A1A1A] border-l-4 border-[#D4AF37] p-4 mb-6">
              {paymentResult.email_sent === true ? (
                <p className="text-[#F5F5F0] text-sm">
                  <strong className="text-green-400">Confirmation email sent!</strong> — Your ticket details have been sent to <strong>{b?.email || "your email"}</strong>. 
                  Please check your <strong>spam/junk folder</strong> if you don't see it within a few minutes.
                </p>
              ) : paymentResult.email_sent === false ? (
                <p className="text-[#F5F5F0] text-sm">
                  <strong className="text-yellow-400">Email could not be sent.</strong> — Please take a screenshot of this page as your ticket confirmation. 
                  Contact us at <strong>book@mixandmingleevents.com</strong> for assistance.
                </p>
              ) : (
                <p className="text-[#F5F5F0] text-sm">
                  <strong>Check your email</strong> — A confirmation email with your ticket details has been sent to <strong>{b?.email || "your email"}</strong>. 
                  Please check your <strong>spam/junk folder</strong> if you don't see it within a few minutes.
                </p>
              )}
            </div>
          )}
          
          {/* Action Button */}
          <div className="text-center">
            <button 
              onClick={() => {
                setPaymentResult(null);
                navigate(`/${slug}`, { replace: true });
              }}
              className={`px-8 py-3 font-bold text-lg transition-colors ${
                isFailed 
                  ? "bg-[#D4AF37] text-[#050505] hover:bg-[#B8943B]" 
                  : "bg-[#1A1A1A] border border-[#333] text-[#F5F5F0] hover:bg-[#222]"
              }`}
              data-testid="payment-result-back-btn"
            >
              {isFailed ? "Try Again" : "Back to Event Page"}
            </button>
          </div>
        </motion.div>
      </main>
    );
  }

  // 404 check — only show after payment result has been checked
  if (error || !page) {
    return (
      <main className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Calendar className="w-16 h-16 text-[#D4AF37] mx-auto mb-6" />
          <h3 className="text-2xl font-semibold text-gray-900 mb-4">Event Not Found</h3>
          <p className="text-gray-700 mb-8">{error || "This event page doesn't exist."}</p>
          <a href="/" className="px-6 py-3 bg-[#D4AF37] text-white font-medium hover:bg-[#B8943B] transition-colors">
            Go Home
          </a>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-white" data-testid="dynamic-event-landing-page">
      {/* Top Banner */}
      {page.banner_tag && (
        <div className="bg-[#0F0F0F] text-white py-3 px-4">
          <div className="max-w-7xl mx-auto flex items-center justify-center gap-4 text-sm">
            <Calendar className="w-4 h-4 text-[#D4AF37]" />
            <span>{page.banner_tag}</span>
            <span className="text-[#D4AF37] uppercase tracking-wider font-semibold">Limited Spots</span>
          </div>
        </div>
      )}

      {/* Sticky Navigation */}
      <nav className="sticky top-0 z-50 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-[#D4AF37]" />
            <span className="text-xl font-semibold text-gray-900">Mix & Mingle</span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <button onClick={() => scrollToSection("experience")} className="text-gray-800 hover:text-gray-900 text-sm font-semibold">Experience</button>
            <button onClick={() => scrollToSection("whats-included")} className="text-gray-800 hover:text-gray-900 text-sm font-semibold">What's Included</button>
            <button onClick={() => scrollToSection("details")} className="text-gray-800 hover:text-gray-900 text-sm font-semibold">Details</button>
            <button onClick={() => scrollToSection("tickets")} className="text-gray-800 hover:text-gray-900 text-sm font-semibold">Tickets</button>
            <button onClick={() => scrollToSection("faq")} className="text-gray-800 hover:text-gray-900 text-sm font-semibold">FAQ</button>
          </div>
          <button 
            onClick={() => scrollToSection("tickets")}
            className="px-5 py-2 bg-[#D4AF37] text-white text-sm font-semibold hover:bg-[#B8943B] transition-colors"
            data-testid="nav-reserve-btn"
          >
            Reserve Your Spot
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-16 md:py-24 px-4">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {page.hero_badge && (
              <div className="inline-flex items-center gap-2 bg-[#FDF6E9] text-[#8B6914] px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <Heart className="w-4 h-4" />
                {page.hero_badge}
              </div>
            )}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif text-[#1a1a1a] leading-tight mb-6 font-bold" data-testid="hero-title">
              {page.title}
            </h1>
            {page.subtitle && (
              <p className="text-lg md:text-xl text-[#374151] mb-8 font-medium">{page.subtitle}</p>
            )}
            
            <div className="flex flex-wrap gap-4 mb-8">
              <div className="flex items-center gap-2 bg-gray-100 px-4 py-2 rounded-lg">
                <Calendar className="w-5 h-5 text-[#D4AF37]" />
                <span className="text-[#1f2937] font-medium">{formatDate(page.date)}</span>
              </div>
              <div className="flex items-center gap-2 bg-gray-100 px-4 py-2 rounded-lg">
                <Clock className="w-5 h-5 text-[#D4AF37]" />
                <span className="text-[#1f2937] font-medium">{page.time}</span>
              </div>
            </div>
            
            <div className="flex items-start gap-2 mb-8">
              <MapPin className="w-5 h-5 text-[#D4AF37] mt-0.5" />
              <div>
                <p className="text-[#1f2937] font-medium">{page.location}</p>
                {page.address && <p className="text-[#1f2937]">{page.address}</p>}
              </div>
            </div>

            <div className="flex flex-wrap gap-4">
              <button 
                onClick={() => scrollToSection("tickets")}
                className="px-8 py-4 bg-[#D4AF37] text-white font-semibold hover:bg-[#B8943B] transition-colors flex items-center gap-2"
                data-testid="hero-reserve-btn"
              >
                Reserve My Spot — From $79
                <ArrowRight className="w-5 h-5" />
              </button>
              <button 
                onClick={() => scrollToSection("tickets")}
                className="px-8 py-4 border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-colors flex items-center gap-2"
              >
                <Gift className="w-5 h-5" />
                Gift This Experience
              </button>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            <img 
              src={page.hero_image} 
              alt={page.title}
              className="w-full rounded-2xl shadow-2xl"
              data-testid="hero-image"
            />
            {/* Feature badges */}
            <div className="absolute -bottom-6 left-4 right-4 flex justify-center gap-4 flex-wrap">
              {["Curated Women's Experience", "Beginner-Friendly", "Beautiful Take-Home Creation", "Premium Atmosphere"].map((badge, i) => (
                <span key={i} className="bg-white px-4 py-2 rounded-full text-xs font-medium text-gray-700 shadow-lg">
                  {badge}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* The Experience Section */}
      <section id="experience" className="py-20 px-4 bg-[#FDFBF7]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#B8943B] uppercase tracking-widest text-sm font-semibold mb-4">The Experience</p>
            <h2 className="text-3xl md:text-4xl font-serif text-[#1a1a1a] mb-4 font-bold">
              {page.experience_title || "More Than Soap-Making."}<br />
              <span className="text-[#1a1a1a]">An Evening You'll Feel.</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {page.experience_items?.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="bg-white p-8 rounded-xl text-center shadow-sm"
              >
                <div className="w-16 h-16 bg-[#FDF6E9] rounded-full flex items-center justify-center mx-auto mb-6 text-[#D4AF37]">
                  {getIcon(item.icon)}
                </div>
                <h3 className="text-lg font-bold text-[#1a1a1a] mb-3">{item.title}</h3>
                <p className="text-[#1f2937]">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What's Included Section */}
      <section id="whats-included" className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#B8943B] uppercase tracking-widest text-sm font-semibold mb-4">Your Ticket Includes</p>
            <h2 className="text-3xl md:text-4xl font-serif text-[#1a1a1a] mb-4 font-bold">
              {page.whats_included_title || "Everything. All of It."}
            </h2>
            <p className="text-[#1f2937] text-lg">{page.whats_included_subtitle || "Just show up — we've handled every detail so you can simply enjoy."}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {page.whats_included?.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="flex items-center gap-3"
              >
                <div className="w-6 h-6 rounded-full bg-[#D4AF37] flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4 text-white" />
                </div>
                <span className="text-[#1f2937] font-medium">{item.text}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Take Home Creation Section */}
      {page.take_home_items?.length > 0 && (
        <section className="py-20 px-4 bg-gradient-to-b from-[#FDFBF7] to-white">
          <div className="max-w-7xl mx-auto text-center">
            <p className="text-[#B8943B] uppercase tracking-widest text-sm font-semibold mb-4">{page.take_home_title || "Your Take-Home Creation"}</p>
            <h2 className="text-3xl md:text-4xl font-serif text-[#1a1a1a] mb-4 font-bold">{page.take_home_subtitle || "Artisan Grape Soap, Made by You"}</h2>
            <p className="text-[#1f2937] mb-12 max-w-2xl mx-auto text-lg">
              Choose your color, craft your masterpiece, and take home a stunning handmade grape cluster soap — a beautiful keepsake from your experience.
            </p>

            <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              {page.take_home_items.map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="relative"
                >
                  {item.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#D4AF37] text-white px-4 py-1 rounded-full text-xs font-medium z-10 flex items-center gap-1">
                      <Sparkles className="w-3 h-3" /> Most Popular
                    </div>
                  )}
                  <div className={`bg-white p-6 rounded-2xl shadow-lg ${item.popular ? 'ring-2 ring-[#D4AF37]' : ''}`}>
                    <img 
                      src={item.image} 
                      alt={item.name}
                      className="w-full h-48 object-contain mb-4"
                    />
                    <p className="text-xs text-[#B8943B] uppercase tracking-widest mb-1 font-semibold">{item.label}</p>
                    <h3 className="text-lg font-bold text-[#1a1a1a]">{item.name}</h3>
                  </div>
                </motion.div>
              ))}
            </div>

            <p className="text-[#374151] mt-8 font-medium">Handmade with love • Premium ingredients • Ready to gift</p>
            <button 
              onClick={() => scrollToSection("tickets")}
              className="mt-6 px-8 py-3 bg-[#0F0F0F] text-white font-semibold hover:bg-gray-800 transition-colors inline-flex items-center gap-2"
            >
              I Want to Make This
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </section>
      )}

      {/* Perfect For Section */}
      {page.perfect_for?.length > 0 && (
        <section className="py-20 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <p className="text-[#B8943B] uppercase tracking-widest text-sm font-semibold mb-4">Perfect For</p>
              <h2 className="text-3xl md:text-4xl font-serif text-[#1a1a1a] font-bold">{page.perfect_for_title || "Is This For You? Absolutely."}</h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {page.perfect_for.map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="bg-white p-6 rounded-xl border border-gray-200 hover:border-[#D4AF37] hover:shadow-lg transition-all"
                >
                  <span className="text-3xl mb-4 block">{item.icon}</span>
                  <h3 className="font-bold text-[#1a1a1a] mb-1">{item.title}</h3>
                  <p className="text-[#1f2937]">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Event Details Section */}
      <section id="details" className="py-20 px-4 bg-[#FDFBF7]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#B8943B] uppercase tracking-widest text-sm font-semibold mb-4">Event Details</p>
            <h2 className="text-3xl md:text-4xl font-serif text-[#1a1a1a] font-bold">Mark Your Calendar</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-5xl mx-auto mb-12">
            <div className="text-center">
              <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm">
                <Calendar className="w-6 h-6 text-[#D4AF37]" />
              </div>
              <p className="text-xs text-[#374151] uppercase tracking-widest mb-1 font-semibold">Date</p>
              <p className="text-[#1f2937] font-bold">{formatDate(page.date)}</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm">
                <Clock className="w-6 h-6 text-[#D4AF37]" />
              </div>
              <p className="text-xs text-[#374151] uppercase tracking-widest mb-1 font-semibold">Time</p>
              <p className="text-[#1f2937] font-bold">{page.time}</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm">
                <MapPin className="w-6 h-6 text-[#D4AF37]" />
              </div>
              <p className="text-xs text-[#374151] uppercase tracking-widest mb-1 font-semibold">Venue</p>
              <p className="text-[#1f2937] font-bold">{page.location}</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm">
                <Car className="w-6 h-6 text-[#D4AF37]" />
              </div>
              <p className="text-xs text-[#374151] uppercase tracking-widest mb-1 font-semibold">Parking</p>
              <p className="text-[#1f2937] font-bold">{page.parking_info || "Free parking"}</p>
            </div>
          </div>

          <div className="grid md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            <div className="text-center">
              <p className="text-xs text-[#374151] uppercase tracking-widest mb-1 font-semibold">Address</p>
              <p className="text-[#1f2937] font-medium">{page.address}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-[#374151] uppercase tracking-widest mb-1 font-semibold">Dress Code</p>
              <p className="text-[#1f2937] font-medium">{page.dress_code || "Elevated casual"}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-[#374151] uppercase tracking-widest mb-1 font-semibold">Duration</p>
              <p className="text-[#1f2937] font-medium">{page.duration || "Approximately 3 hours"}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-[#374151] uppercase tracking-widest mb-1 font-semibold">Experience</p>
              <p className="text-[#1f2937] font-medium">{page.experience_level || "Beginner-friendly"}</p>
            </div>
          </div>

          {/* Urgency Messages */}
          <div className="mt-12 max-w-3xl mx-auto space-y-4">
            {page.limited_seats_message && (
              <div className="bg-white p-4 rounded-lg border-l-4 border-[#D4AF37]">
                <h4 className="font-semibold text-gray-900 mb-1">Limited Intimate Seating</h4>
                <p className="text-gray-800 text-sm">{page.limited_seats_message}</p>
              </div>
            )}
            {page.urgency_note && (
              <div className="bg-white p-4 rounded-lg border-l-4 border-[#D4AF37]">
                <h4 className="font-semibold text-gray-900 mb-1">Mother's Day Weekend</h4>
                <p className="text-gray-800 text-sm">{page.urgency_note}</p>
              </div>
            )}
            <div className="bg-white p-4 rounded-lg border-l-4 border-[#D4AF37]">
              <h4 className="font-semibold text-gray-900 mb-1">Groups Fill Fast</h4>
              <p className="text-gray-800 text-sm">When one woman books, she texts her friends. Seats disappear quickly.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Tickets Section */}
      <section id="tickets" className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#B8943B] uppercase tracking-widest text-sm font-semibold mb-4">Tickets</p>
            <h2 className="text-3xl md:text-4xl font-serif text-[#1a1a1a] font-bold">Choose Your Experience</h2>
          </div>

          {/* Individual Tickets */}
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto mb-12">
            {page.ticket_options?.map((ticket, i) => {
              const isDisabled = ticket.status === "sold_out" || ticket.status === "coming_soon";
              const statusBadge = {
                limited: { text: "🔥 Limited Spots", color: "bg-orange-500" },
                almost_sold_out: { text: "⚡ Almost Sold Out", color: "bg-red-500" },
                sold_out: { text: "❌ Sold Out", color: "bg-gray-500" },
                coming_soon: { text: "🕐 Coming Soon", color: "bg-blue-500" }
              }[ticket.status];

              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className={`relative bg-white rounded-2xl p-8 border-2 ${
                    isDisabled ? 'border-gray-200 opacity-75' :
                    ticket.popular ? 'border-[#D4AF37] shadow-xl' : 'border-gray-100 hover:border-gray-300'
                  } transition-all`}
                >
                  {ticket.badge && !statusBadge && (
                    <div className="absolute -top-3 left-4 bg-[#D4AF37] text-white px-3 py-1 rounded-full text-xs font-medium">
                      {ticket.badge}
                    </div>
                  )}
                  {statusBadge && (
                    <div className={`absolute -top-3 left-4 ${statusBadge.color} text-white px-3 py-1 rounded-full text-xs font-medium`}>
                      {statusBadge.text}
                    </div>
                  )}
                  {ticket.popular && !isDisabled && (
                    <div className="absolute -top-3 right-4 bg-[#0F0F0F] text-white px-3 py-1 rounded-full text-xs font-medium">
                      Most Popular
                    </div>
                  )}

                  <h3 className="text-xl font-bold text-[#1a1a1a] mb-2">{ticket.name}</h3>
                  <p className="text-[#1f2937] mb-4">{ticket.description}</p>

                  <div className="mb-6">
                    {ticket.original_price && (
                      <span className="text-[#9ca3af] line-through text-lg mr-2">${ticket.original_price}</span>
                    )}
                    <span className="text-4xl font-bold text-[#1a1a1a]">${ticket.price}</span>
                  </div>

                  <ul className="space-y-3 mb-8">
                    {ticket.perks?.map((perk, j) => (
                      <li key={j} className="flex items-start gap-2 text-[#1f2937]">
                        <Check className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                        <span>{perk}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => !isDisabled && openCheckout(ticket)}
                    disabled={isDisabled}
                    className={`w-full py-3 font-semibold transition-colors ${
                      isDisabled 
                        ? 'bg-gray-200 text-gray-700 cursor-not-allowed'
                        : 'bg-[#D4AF37] text-white hover:bg-[#B8943B]'
                    }`}
                    data-testid={`ticket-${ticket.name}-btn`}
                  >
                    {ticket.status === "sold_out" ? "Sold Out" :
                     ticket.status === "coming_soon" ? "Coming Soon" :
                     ticket.popular ? 'Reserve My Spot' : 'Select'}
                  </button>
                </motion.div>
              );
            })}
          </div>

          {/* Group Tickets */}
          {page.group_tickets?.length > 0 && (
            <div className="max-w-3xl mx-auto">
              <h3 className="text-center text-lg font-bold text-[#1a1a1a] mb-6">Coming with friends?</h3>
              <div className="grid md:grid-cols-2 gap-6">
                {page.group_tickets.map((ticket, i) => (
                  <div 
                    key={i}
                    className="bg-white p-6 rounded-xl border border-gray-200 hover:border-[#D4AF37] transition-all"
                  >
                    <h4 className="font-bold text-[#1a1a1a] mb-1">{ticket.name}</h4>
                    <p className="text-[#1f2937] mb-3">{ticket.description}</p>
                    <div className="flex items-end justify-between">
                      <div>
                        <span className="text-2xl font-bold text-[#1a1a1a]">${ticket.price}</span>
                        <span className="text-[#1f2937] ml-2">(${ticket.per_person}/person)</span>
                      </div>
                      <span className="text-[#D4AF37] font-bold">{ticket.savings}</span>
                    </div>
                    <button
                      onClick={() => openCheckout(ticket)}
                      className="w-full mt-4 py-3 bg-[#D4AF37] text-white font-semibold hover:bg-[#B8943B] transition-colors"
                      data-testid={`group-ticket-${ticket.name}-btn`}
                    >
                      Book for {ticket.seats || ticket.quantity}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* FAQ Section */}
      {page.faqs?.length > 0 && (
        <section id="faq" className="py-20 px-4 bg-[#FDFBF7]">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <p className="text-[#B8943B] uppercase tracking-widest text-sm font-semibold mb-4">Questions?</p>
              <h2 className="text-3xl md:text-4xl font-serif text-[#1a1a1a] font-bold">We've Got Answers</h2>
            </div>

            <div className="space-y-4">
              {page.faqs.map((faq, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: i * 0.05 }}
                  className="bg-white rounded-xl overflow-hidden shadow-sm"
                >
                  <button
                    onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}
                    className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                    data-testid={`faq-${i}`}
                  >
                    <span className="font-bold text-[#1a1a1a]">{faq.question}</span>
                    {expandedFaq === i ? (
                      <ChevronUp className="w-5 h-5 text-[#D4AF37]" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-[#374151]" />
                    )}
                  </button>
                  <AnimatePresence>
                    {expandedFaq === i && (
                      <motion.div
                        initial={{ height: 0 }}
                        animate={{ height: "auto" }}
                        exit={{ height: 0 }}
                        className="overflow-hidden"
                      >
                        <p className="px-6 pb-4 text-[#1f2937]">{faq.answer}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Final CTA Section */}
      <section className="py-20 px-4 bg-[#0F0F0F]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-serif text-white mb-4">
            {page.cta_title || "You've Read This Far."}<br />
            <span className="text-[#D4AF37]">{page.cta_subtitle || "You Already Know You Want In."}</span>
          </h2>
          <div className="flex flex-wrap justify-center gap-4 mt-8">
            <button 
              onClick={() => scrollToSection("tickets")}
              className="px-8 py-4 bg-[#D4AF37] text-white font-semibold hover:bg-[#B8943B] transition-colors"
              data-testid="cta-reserve-btn"
            >
              Yes — I'm In
            </button>
            <button 
              onClick={() => scrollToSection("tickets")}
              className="px-8 py-4 border border-white text-white font-semibold hover:bg-white hover:text-[#0F0F0F] transition-colors"
            >
              Gift a Ticket
            </button>
          </div>
        </div>
      </section>

      {/* Checkout Modal */}
      <AnimatePresence>
        {showCheckoutModal && selectedTicket && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
            onClick={() => setShowCheckoutModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold text-gray-900">Complete Your Booking</h3>
                  <button onClick={() => setShowCheckoutModal(false)} className="text-gray-400 hover:text-gray-800">
                    <X className="w-6 h-6" />
                  </button>
                </div>
                {/* Trust Badges */}
                <div className="flex items-center gap-4 mt-3">
                  <div className="flex items-center gap-1 text-green-600 text-xs">
                    <Shield className="w-3 h-3" />
                    <span>SSL Secure</span>
                  </div>
                  <div className="flex items-center gap-1 text-blue-600 text-xs">
                    <BadgeCheck className="w-3 h-3" />
                    <span>Verified Business</span>
                  </div>
                  <div className="flex items-center gap-1 text-gray-700 text-xs">
                    <Lock className="w-3 h-3" />
                    <span>Encrypted</span>
                  </div>
                </div>
              </div>

              <div className="p-6">
                {/* Ticket Summary */}
                <div className="bg-[#FDFBF7] p-4 rounded-lg mb-6">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="font-semibold text-gray-900">{selectedTicket.name}</p>
                      <p className="text-gray-700 text-sm">{page.title || page.event_name}</p>
                    </div>
                    <p className="text-lg font-bold text-gray-900">${selectedTicket.price}</p>
                  </div>
                  <p className="text-xs text-gray-700">{formatDate(page.date)} • {page.time}</p>
                  {selectedTicket.seats && (
                    <p className="text-xs text-[#D4AF37] mt-1">Group ticket for {selectedTicket.seats} people</p>
                  )}
                </div>

                {/* Form */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-1">Full Name *</label>
                    <input
                      type="text"
                      value={checkoutForm.name}
                      onChange={(e) => setCheckoutForm(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#D4AF37] text-gray-900 font-medium placeholder-gray-400"
                      placeholder="Jane Doe"
                      data-testid="checkout-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-1">Email *</label>
                    <input
                      type="email"
                      value={checkoutForm.email}
                      onChange={(e) => setCheckoutForm(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#D4AF37] text-gray-900 font-medium placeholder-gray-400"
                      placeholder="jane@example.com"
                      data-testid="checkout-email"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-1">Phone *</label>
                    <input
                      type="tel"
                      value={checkoutForm.phone}
                      onChange={(e) => setCheckoutForm(prev => ({ ...prev, phone: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#D4AF37] text-gray-900 font-medium placeholder-gray-400"
                      placeholder="(780) 555-1234"
                      data-testid="checkout-phone"
                    />
                  </div>

                  {!selectedTicket.quantity && !selectedTicket.seats && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-1">Quantity</label>
                      <select
                        value={checkoutForm.quantity}
                        onChange={(e) => setCheckoutForm(prev => ({ ...prev, quantity: parseInt(e.target.value) }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#D4AF37] text-gray-900 font-medium bg-white"
                        data-testid="checkout-quantity"
                      >
                        {[1, 2, 3, 4, 5].map(n => (
                          <option key={n} value={n}>{n}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Coupon */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-1">Coupon Code</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={checkoutForm.couponCode}
                        onChange={(e) => setCheckoutForm(prev => ({ ...prev, couponCode: e.target.value.toUpperCase() }))}
                        className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#D4AF37] text-gray-900 font-bold uppercase placeholder-gray-400"
                        placeholder="SAVE10"
                        data-testid="checkout-coupon"
                      />
                      <button
                        onClick={validateCoupon}
                        className="px-4 py-3 bg-[#D4AF37] text-white font-semibold hover:bg-[#B8943B] transition-colors rounded-lg"
                      >
                        Apply
                      </button>
                    </div>
                    {couponApplied && (
                      <p className="text-green-600 text-sm font-semibold mt-1">
                        ✓ Coupon applied: {couponApplied.discount_type === "percentage" 
                          ? `${couponApplied.discount_value}% off` 
                          : `$${couponApplied.discount_value} off`}
                      </p>
                    )}
                    {couponError && (
                      <p className="text-red-600 text-sm font-semibold mt-1">{couponError}</p>
                    )}
                  </div>
                </div>

                {/* Total */}
                <div className="mt-6 pt-6 border-t border-gray-100">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-800">Subtotal</span>
                    <span className="text-gray-900">
                      ${selectedTicket.seats 
                        ? selectedTicket.price.toFixed(2) 
                        : (selectedTicket.price * checkoutForm.quantity).toFixed(2)}
                    </span>
                  </div>
                  {couponApplied && (
                    <div className="flex justify-between items-center mb-2 text-green-600">
                      <span>Discount</span>
                      <span>-${(
                        (selectedTicket.seats ? selectedTicket.price : selectedTicket.price * checkoutForm.quantity) 
                        - calculateTotal()
                      ).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center text-lg font-bold">
                    <span className="text-gray-900">Total</span>
                    <span className="text-[#D4AF37]">${calculateTotal().toFixed(2)} CAD</span>
                  </div>
                </div>

                <button
                  onClick={handleCheckout}
                  disabled={checkoutLoading}
                  className="w-full mt-6 py-4 bg-[#D4AF37] text-white font-semibold hover:bg-[#B8943B] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="checkout-submit-btn"
                >
                  {checkoutLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5" />
                      Proceed to Payment
                    </>
                  )}
                </button>

                {/* Payment Methods & Security */}
                <div className="mt-4 pt-4 border-t border-gray-100">
                  {/* Trust Badges */}
                  <div className="flex items-center justify-center gap-4 mb-4">
                    <div className="flex items-center gap-1.5 text-green-600">
                      <Shield className="w-4 h-4" />
                      <span className="text-xs font-medium">SSL Secured</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-blue-600">
                      <BadgeCheck className="w-4 h-4" />
                      <span className="text-xs font-medium">Verified Business</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-purple-600">
                      <Lock className="w-4 h-4" />
                      <span className="text-xs font-medium">256-bit Encryption</span>
                    </div>
                  </div>
                  
                  {/* Payment Methods */}
                  <div className="flex items-center justify-center gap-3 mb-3">
                    <div className="bg-[#1A1F71] text-white text-xs font-bold px-2.5 py-1.5 rounded">VISA</div>
                    <div className="bg-gradient-to-r from-[#EB001B] to-[#F79E1B] text-white text-xs font-bold px-2.5 py-1.5 rounded">MC</div>
                    <div className="bg-[#006FCF] text-white text-xs font-bold px-2.5 py-1.5 rounded">AMEX</div>
                    <div className="bg-black text-white text-xs font-bold px-2.5 py-1.5 rounded flex items-center gap-1">
                      <span>Apple</span><span className="font-light">Pay</span>
                    </div>
                    <div className="bg-[#5F6368] text-white text-xs font-bold px-2.5 py-1.5 rounded flex items-center gap-1">
                      <span className="text-[#4285F4]">G</span><span>Pay</span>
                    </div>
                  </div>
                  
                  {/* Stripe Badge */}
                  <div className="flex items-center justify-center gap-2 text-gray-700 bg-gray-50 py-2 rounded-lg">
                    <Lock className="w-3.5 h-3.5" />
                    <span className="text-xs">Secure checkout powered by</span>
                    <span className="text-[#635BFF] font-bold text-sm">stripe</span>
                  </div>
                  
                  {/* Guarantee */}
                  <p className="text-center text-xs text-gray-400 mt-3">
                    100% Money-Back Guarantee • Instant Confirmation • No Hidden Fees
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
};

export default DynamicEventLandingPage;
